// "3 steps to start" — the guide every new account gets instead of the forced full-length
// tutorial: (1) a campaign (the "create campaign" button opens the app's own form),
// (2) a post, (3) connect the Facebook profile. Each step: a short video of just that step
// (desktop: that scene of the narrated walkthrough; mobile: a phone-screen recording), a
// button that opens the task, automatic detection when it's really done (the API checks the
// data), "watch again", and help (support WhatsApp / a support ticket). Every interaction is
// sent to /user/onboarding-guide/event so /admin/ads shows where signups get stuck.
// Desktop: a panel in the corner. Mobile: a bottom sheet.
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import axios from 'axios'
import {useNavigate} from 'react-router-dom'
import StepDemo from '../../pages/tutorial-demo/StepDemo'
import Yad2Box, {Yad2Status, yad2Blocks} from './Yad2Box'
import './OnboardingGuide.scss'

const API_URL = process.env.REACT_APP_API_URL
const SUPPORT_WA = '972722210190'

type StepId = 'campaign' | 'post' | 'facebook'
type Status = Yad2Status & {
  show: boolean
  all_done: boolean
  current: StepId | null
  steps: {id: StepId; done: boolean}[]
  prebuilt_campaigns?: string[]
  watched?: StepId[]
  connect_url: string | null
  all_set?: boolean
  // Website signup, business not known yet: welcome + "what do you do?" before the steps.
  intro?: boolean
}

const OCCUPATIONS: {id: string; label: string}[] = [
  {id: 'real_estate', label: '🏠 נדל״ן'},
  {id: 'cars', label: '🚗 רכב'},
  {id: 'hr', label: '👷 כוח אדם'},
  {id: 'vacation_rental', label: '🏖️ השכרת נופש'},
  {id: 'other', label: '✏️ אחר'},
]

const STEPS: Record<StepId, {title: string; sub: string; scenes: string[]; mobileVideo?: string}> =
  {
    campaign: {title: 'יוצרים קמפיין', sub: 'תיקייה לפוסטים לפי עיר או תחום', scenes: ['campaign']},
    post: {title: 'מוסיפים פוסט', sub: 'המודעה שתצא לקבוצות', scenes: ['post']},
    facebook: {title: 'מחברים את הפייסבוק', sub: 'מכאן המערכת מתחילה לפרסם', scenes: ['connect', 'reset', 'approve']},
  }
// Phone-screen recordings of each step (real app, narrated). A step without one plays the
// desktop scene.
const MEDIA = 'https://zipper.ams3.cdn.digitaloceanspaces.com/zipper/site-media/fbzipper/wa'
const MOBILE_VIDEOS: Partial<Record<StepId, string>> = {
  campaign: `${MEDIA}/guide-mobile-campaign.mp4?v=3`,
  post: `${MEDIA}/guide-mobile-post.mp4?v=4`,
  // The narrated connect → forgot password → approve-on-phone chapters (the same video the
  // WhatsApp bot sends), not the short connect-only phone recording.
  facebook: 'https://zipper.ams3.cdn.digitaloceanspaces.com/zipper/site-media/fbzipper/wa/connect-tutorial-v4.mp4',
}

// Phones (iOS especially) refuse to start a video with sound unless play() comes straight
// from the tap — which it doesn't here (the <video> mounts after it), so the video sat at
// 0:00. Try with sound; if refused, play muted with a "turn on sound" button.
const PhoneVideo: React.FC<{src: string; onEnd: () => void}> = ({src, onEnd}) => {
  const ref = useRef<HTMLVideoElement>(null)
  const [silent, setSilent] = useState(false)
  // Watching is required to unlock the step: seeking past what was already played jumps back.
  const maxT = useRef(0)
  useEffect(() => {
    const v = ref.current
    if (!v) return
    maxT.current = 0
    v.play().catch(() => {
      v.muted = true
      setSilent(true)
      v.play().catch(() => {})
    })
  }, [src])
  return (
    <div className='og-video-wrap'>
      <video
        className='og-video'
        ref={ref}
        src={src}
        controls
        controlsList='noplaybackrate'
        playsInline
        onEnded={onEnd}
        onTimeUpdate={(e) => {
          const v = e.currentTarget
          if (!v.seeking && v.currentTime > maxT.current && v.currentTime - maxT.current < 2)
            maxT.current = v.currentTime
        }}
        onSeeking={(e) => {
          const v = e.currentTarget
          if (v.currentTime > maxT.current + 1) v.currentTime = maxT.current
        }}
        onRateChange={(e) => {
          if (e.currentTarget.playbackRate > 1) e.currentTarget.playbackRate = 1
        }}
      />
      {silent && (
        <button
          className='og-unmute'
          onClick={() => {
            const v = ref.current
            if (!v) return
            v.muted = false
            setSilent(false)
            if (v.currentTime > 1) v.currentTime = 0
            v.play().catch(() => {})
          }}
        >
          🔊 הפעלת קול
        </button>
      )}
    </div>
  )
}

const isMobile = () => window.innerWidth <= 768

const OnboardingGuide: React.FC = () => {
  const [status, setStatus] = useState<Status | null>(null)
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem('og_collapsed') !== '1'
    } catch {
      return true
    }
  })
  const [watching, setWatching] = useState<StepId | null>(null)
  const [watchedLocal, setWatchedLocal] = useState<StepId[]>([])
  const [mobile, setMobile] = useState(isMobile())
  const navigate = useNavigate()
  const shownRef = useRef(false)
  const prevDone = useRef<Record<string, boolean>>({})
  const device = mobile ? 'mobile' : 'desktop'
  const force = new URLSearchParams(window.location.search).get('guide') === '1'

  const track = useCallback(
    (step: string, event: string) => {
      axios.post(`${API_URL}/user/onboarding-guide/event`, {step, event, device}).catch(() => {})
    },
    [device]
  )

  const load = useCallback(async () => {
    try {
      const {data} = await axios.get(`${API_URL}/user/onboarding-guide`, {
        params: {device, force: force ? '1' : undefined},
      })
      if (!data.success) return
      // A step just turned done → short celebration, move on.
      const justDone = (data.steps || []).some(
        (s: any) => s.done && prevDone.current[s.id] === false
      )
      ;(data.steps || []).forEach((s: any) => (prevDone.current[s.id] = s.done))
      if (justDone) setWatching(null)
      setStatus(data)
    } catch {
      // not logged in / network — stay hidden
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, force, status])

  useEffect(() => {
    load()
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') load()
    }, 8000)
    const onFocus = () => load()
    const onResize = () => setMobile(isMobile())
    window.addEventListener('focus', onFocus)
    window.addEventListener('resize', onResize)
    return () => {
      clearInterval(t)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('resize', onResize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Hide while a task window is open (create campaign / add post), back when it closes.
  const [modalOpen, setModalOpen] = useState(false)
  useEffect(() => {
    const t = setInterval(() => setModalOpen(!!document.querySelector('.modal.show, .fb-edit-modal')), 500)
    return () => clearInterval(t)
  }, [])

  // Mobile, open (or one of the full-screen popups): the page behind must not scroll. iOS
  // Safari ignores overflow:hidden on <body>, so the body is pinned in place (position:fixed
  // at the current scroll offset) and put back exactly where it was on close.
  const fullScreen =
    !modalOpen && ((mobile && open && !!status?.show) || !!(status?.intro && status.show) || !!status?.all_set)
  useEffect(() => {
    if (!fullScreen) return
    const y = window.scrollY
    const b = document.body.style
    const prev = {position: b.position, top: b.top, left: b.left, right: b.right, width: b.width, overflow: b.overflow}
    document.documentElement.classList.add('og-lock')
    document.body.classList.add('og-lock')
    b.position = 'fixed'
    b.top = `-${y}px`
    b.left = '0'
    b.right = '0'
    b.width = '100%'
    b.overflow = 'hidden'
    return () => {
      Object.assign(b, prev)
      document.documentElement.classList.remove('og-lock')
      document.body.classList.remove('og-lock')
      window.scrollTo(0, y)
    }
  }, [fullScreen])

  // Intro (welcome → "what do you do?") before the steps, for website signups.
  const [introStep, setIntroStep] = useState<'welcome' | 'occupation' | 'other'>('welcome')
  const [introOther, setIntroOther] = useState('')
  const [introBusy, setIntroBusy] = useState(false)
  const introShownRef = useRef(false)
  useEffect(() => {
    if (status?.intro && !introShownRef.current) {
      introShownRef.current = true
      track('guide', 'intro_shown')
    }
  }, [status, track])

  const successShownRef = useRef(false)
  useEffect(() => {
    if (status?.all_set && !successShownRef.current) {
      successShownRef.current = true
      track('guide', 'success_shown')
    }
  }, [status, track])

  useEffect(() => {
    if (status?.show && !shownRef.current) {
      shownRef.current = true
      track('guide', 'shown')
    }
  }, [status, track])

  if (status?.all_set && !modalOpen) {
    const close = (toDashboard: boolean) => {
      track('guide', toDashboard ? 'success_dashboard' : 'success_dismiss')
      axios.post(`${API_URL}/user/onboarding-guide/success-dismiss`).catch(() => {})
      setStatus({...status, all_set: false})
      if (toDashboard) navigate('/stats')
    }
    return createPortal(
      <div className='og-success' dir='rtl' onClick={() => close(false)}>
        <div className='og-success-card' onClick={(e) => e.stopPropagation()}>
          <button className='og-success-x' onClick={() => close(false)} aria-label='סגירה'>
            ✕
          </button>
          <div className='og-success-top'>
            <div className='og-success-badge'>🎉</div>
            <h3>כל הכבוד, החשבון מוכן!</h3>
            <p>השלמת את 3 הצעדים, והמערכת מתחילה לפרסם בשבילך.</p>
          </div>
          <ul className='og-success-list'>
            <li>
              <span>🎯</span>
              <div>
                <b>אנחנו בוחרים בשבילך את הקבוצות הכי טובות</b>
                <small>המערכת מתאימה את הקבוצות לתחום ולאזור שלך, ומפרסמת בהן לאורך היום.</small>
              </div>
            </li>
            <li>
              <span>➕</span>
              <div>
                <b>כדאי להוסיף עוד פוסטים וקמפיינים</b>
                <small>יותר מודעות ויותר אזורים = יותר חשיפה ויותר פניות.</small>
              </div>
            </li>
            <li>
              <span>📊</span>
              <div>
                <b>הכול במעקב בדשבורד</b>
                <small>כמה פוסטים פורסמו, באילו קבוצות, ומי הגיב — הכול בעמוד הנתונים.</small>
              </div>
            </li>
          </ul>
          <div className='og-success-actions'>
            <button className='btn btn-primary' onClick={() => close(false)}>
              הבנתי, תודה
            </button>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  if (status?.intro && status.show && !modalOpen) {
    const pick = async (occupation: string, other?: string) => {
      setIntroBusy(true)
      try {
        await axios.post(`${API_URL}/user/onboarding-guide/intro`, {occupation, other: other || ''})
      } catch {
        // best effort — the guide still opens
      }
      track('guide', 'intro_done')
      setIntroBusy(false)
      setStatus({...status, intro: false, ask_real_estate: false, real_estate: occupation === 'real_estate'} as Status)
      setOpen(true)
      load()
    }
    return createPortal(
      <div className='og-success' dir='rtl'>
        <div className='og-success-card'>
          {introStep === 'welcome' ? (
            <>
              <div className='og-success-top'>
                <div className='og-success-badge'>🚀</div>
                <h3>ברוכים הבאים ל-FBZipper!</h3>
                <p>החשבון שלך נפתח, עם 7 ימי ניסיון חינם, בלי כרטיס אשראי ובלי התחייבות.</p>
              </div>
              <ul className='og-success-list'>
                <li>
                  <span>📣</span>
                  <div>
                    <b>מפרסמים בשבילך אוטומטית</b>
                    <small>בקבוצות פייסבוק ובמרקטפלייס, לאורך כל היום.</small>
                  </div>
                </li>
                <li>
                  <span>💬</span>
                  <div>
                    <b>עונים אוטומטית לתגובות</b>
                    <small>כל מי שמגיב על הפוסט מקבל תשובה, והפניות מגיעות אליך.</small>
                  </div>
                </li>
                <li>
                  <span>👣</span>
                  <div>
                    <b>3 צעדים קצרים ומתחילים</b>
                    <small>קמפיין, פוסט וחיבור הפייסבוק, עם סרטון קצר לכל צעד.</small>
                  </div>
                </li>
              </ul>
              <div className='og-success-actions'>
                <button className='btn btn-primary' onClick={() => setIntroStep('occupation')}>
                  בואו נתחיל
                </button>
              </div>
            </>
          ) : introStep === 'occupation' ? (
            <>
              <div className='og-success-top'>
                <div className='og-success-badge'>💼</div>
                <h3>במה אתה עוסק?</h3>
                <p>כך נתאים לך את הקבוצות ואת ההגדרות.</p>
              </div>
              <div className='d-grid gap-2 mt-2'>
                {OCCUPATIONS.map((o) => (
                  <button
                    key={o.id}
                    className='btn btn-light-primary fw-bold fs-5 py-3'
                    disabled={introBusy}
                    onClick={() => (o.id === 'other' ? setIntroStep('other') : pick(o.id))}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className='og-success-top'>
                <div className='og-success-badge'>✏️</div>
                <h3>מה הולכים לפרסם?</h3>
                <p>במילה או שתיים, למשל: שיפוצים, קורסים, מזון.</p>
              </div>
              <input
                className='form-control form-control-lg mb-3'
                autoFocus
                value={introOther}
                onChange={(e) => setIntroOther(e.target.value)}
                maxLength={120}
              />
              <div className='og-success-actions'>
                <button
                  className='btn btn-primary'
                  disabled={introBusy || !introOther.trim()}
                  onClick={() => pick('other', introOther)}
                >
                  המשך
                </button>
                <button className='btn btn-light' onClick={() => setIntroStep('occupation')}>
                  חזרה
                </button>
              </div>
            </>
          )}
        </div>
      </div>,
      document.body
    )
  }

  if (!status || !status.show) return null

  const doneCount = status.steps.filter((s) => s.done).length
  const prebuilt = status.prebuilt_campaigns || []
  const current = status.current

  const toggleOpen = () => {
    const next = !open
    setOpen(next)
    track('guide', next ? 'expand' : 'collapse')
    try {
      localStorage.setItem('og_collapsed', next ? '0' : '1')
    } catch {}
  }

  // Open the real task in the app.
  const clickWhenReady = (selector: string, tries = 20) => {
    const el = document.querySelector(selector) as HTMLElement | null
    if (el) return el.click()
    if (tries > 0) setTimeout(() => clickWhenReady(selector, tries - 1), 300)
  }
  const startTask = (step: StepId) => {
    track(step, 'task_start')
    if (mobile) setOpen(false)
    if (step === 'campaign') {
      navigate('/campaigns')
      setTimeout(() => clickWhenReady('[data-onboarding="create-campaign"]'), 400)
    } else if (step === 'post') {
      if (!window.location.pathname.startsWith('/campaigns')) navigate('/campaigns')
      setTimeout(() => pointAt('[data-onboarding="add-post"]', 'לוחצים כאן כדי להוסיף פוסט'), 500)
    } else if (step === 'facebook') {
      // The /c/:code connect page: a popup window on a computer (opened straight from the
      // click so it isn't blocked); on phones the same tab — in-app browsers (WhatsApp's,
      // where the login link opens) silently drop popups and new tabs. Blocked popup → same
      // tab too. No link yet → the profile card's connect button.
      const url = status?.connect_url
      if (url) {
        if (!mobile) {
          const w = 520
          const h = 760
          const left = Math.max(0, window.screenX + (window.outerWidth - w) / 2)
          const top = Math.max(0, window.screenY + (window.outerHeight - h) / 2)
          const win = window.open(url, 'fbz-connect', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`)
          if (win) return
        }
        window.location.href = url
        return
      }
      if (!window.location.pathname.startsWith('/campaigns')) navigate('/campaigns')
      setTimeout(() => clickWhenReady('[data-onboarding="connect-btn"]'), 500)
    }
  }

  // Steps 2–3: scroll the page to the real button, make it flash and put an arrow with a
  // short label above it — the customer clicks it themselves. Gone after 8s or on click.
  const pointAt = (selector: string, label: string, tries = 20) => {
    const el = Array.from(document.querySelectorAll(selector)).find(
      (e) => (e as HTMLElement).offsetParent !== null
    ) as HTMLElement | undefined
    if (!el) {
      if (tries > 0) setTimeout(() => pointAt(selector, label, tries - 1), 300)
      return
    }
    document.querySelectorAll('.og-pointer').forEach((p) => p.remove())
    el.scrollIntoView({behavior: 'smooth', block: 'center'})
    el.classList.add('og-flash')
    const arrow = document.createElement('div')
    arrow.className = 'og-pointer'
    arrow.innerHTML = '<span></span><b>👇</b>'
    ;(arrow.firstChild as HTMLElement).textContent = label
    document.body.appendChild(arrow)
    const place = () => {
      const r = el.getBoundingClientRect()
      arrow.style.left = r.left + r.width / 2 + 'px'
      arrow.style.top = r.top + 'px'
    }
    place()
    const tick = setInterval(place, 50)
    const done = () => {
      clearInterval(tick)
      arrow.remove()
      el.classList.remove('og-flash')
      el.removeEventListener('click', done)
    }
    el.addEventListener('click', done)
    setTimeout(done, 8000)
  }

  const help = (step: StepId) => {
    track(step, 'help_whatsapp')
    const text = `היי, אני צריך/ה עזרה בשלב "${STEPS[step].title}" 🙏`
    window.open(
      `https://wa.me/${SUPPORT_WA}?text=${encodeURIComponent(text)}`,
      '_blank',
      'noopener'
    )
  }
  const ticket = (step: StepId) => {
    track(step, 'help_ticket')
    navigate('/support')
  }

  // Step 3 (Facebook): the button unlocks once its video has played to the end (server:
  // video_end events, so it holds across devices; local: right away, without waiting for the
  // next poll). Steps 1 (campaign) and 2 (post) have no required video.
  const watched = (step: StepId) =>
    step === 'campaign' || step === 'post' || watchedLocal.includes(step) || !!status?.watched?.includes(step)
  const ended = (step: StepId) => {
    setWatchedLocal((w) => (w.includes(step) ? w : [...w, step]))
    track(step, 'video_end')
  }

  const watch = (step: StepId, replay: boolean) => {
    setWatching(step)
    track(step, replay ? 'video_replay' : 'video_play')
  }

  const video = (step: StepId) => {
    const mv = mobile ? MOBILE_VIDEOS[step] : undefined
    if (mv) return <PhoneVideo key={step} src={mv} onEnd={() => ended(step)} />
    // Desktop: the walkthrough scene, large in the middle of the screen (in the corner panel
    // the 1280px app screen shrank to unreadable).
    return createPortal(
      <div className='og-theater' dir='rtl' onClick={() => setWatching(null)}>
        <div className='og-theater-box' onClick={(e) => e.stopPropagation()}>
          <div className='og-theater-head'>
            <b>{STEPS[step].title}</b>
            <button onClick={() => setWatching(null)} aria-label='סגירה'>
              ✕
            </button>
          </div>
          <StepDemo
            key={step}
            sceneIds={STEPS[step].scenes}
            autoPlay
            onEnd={() => ended(step)}
          />
        </div>
      </div>,
      document.body
    )
  }

  return (
    <div
      className={`og ${mobile ? 'og-mobile' : 'og-desktop'} ${open ? 'og-open' : ''} ${modalOpen ? 'og-hidden' : ''}`}
      dir='rtl'
    >
      <button className='og-head' onClick={toggleOpen}>
        <span className='og-ring' style={{['--p' as any]: `${(doneCount / 3) * 100}%`}}>
          {doneCount}/3
        </span>
        <span className='og-head-text'>
          <b>3 צעדים ומתחילים לפרסם</b>
          {current && <small>עכשיו: {STEPS[current].title}</small>}
        </span>
        <i className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-up'}`} />
      </button>

      {open && (
        <div className='og-body'>
          {status.steps.map((s, i) => {
            const meta = STEPS[s.id]
            const active = s.id === current
            return (
              <div
                key={s.id}
                className={`og-step ${s.done ? 'done' : ''} ${active ? 'active' : ''}`}
              >
                <div className='og-step-row'>
                  <span className='og-num'>{s.done ? '✓' : i + 1}</span>
                  <span className='og-step-text'>
                    <b>
                      {s.done && s.id === 'campaign' && prebuilt.length
                        ? 'הקמפיינים שלך כבר מוכנים'
                        : meta.title}
                    </b>
                    <small>
                      {s.done && s.id === 'campaign' && prebuilt.length
                        ? `פתחנו לך כבר ${prebuilt.length === 1 ? 'קמפיין' : prebuilt.length + ' קמפיינים'}: ${prebuilt.join(', ')} ✅`
                        : s.done && s.id === 'post' && status.yad2?.enabled
                        ? status.yad2.importing
                          ? 'מייבאים את הנכסים שלך מיד2… ⏳'
                          : 'הנכסים שלך יובאו מיד2 ✅'
                        : s.done
                        ? 'בוצע'
                        : meta.sub}
                    </small>
                  </span>
                  {!active && !s.done && <small className='og-later'>אחר כך</small>}
                </div>

                {active && (
                  <div className='og-step-body'>
                    {s.id === 'post' && <Yad2Box status={status} reload={load} track={track} />}
                    {!(s.id === 'post' && yad2Blocks(status)) && (<>
                    {watching === s.id ? (
                      video(s.id)
                    ) : (
                      <button
                        className={`og-watch ${s.id === 'facebook' && !watched(s.id) ? 'og-watch-pulse' : ''}`}
                        onClick={() => watch(s.id, false)}
                      >
                        <span className='og-watch-play'>▶</span>
                        <span>
                          <b>צפו איך עושים את זה</b>
                          <small>סרטון קצר, פחות מ-20 שניות</small>
                        </span>
                      </button>
                    )}


                    {!watched(s.id) && (
                      <div className='og-locked'>🔒 צפייה בסרטון עד הסוף פותחת את השלב</div>
                    )}
                    <button
                      className='btn btn-primary w-100'
                      disabled={!watched(s.id)}
                      onClick={() => startTask(s.id)}
                    >
                      {s.id === 'campaign'
                        ? 'יצירת קמפיין'
                        : s.id === 'post'
                        ? 'יצירת הפוסט הראשון'
                        : 'התחברות לפרופיל הפייסבוק'}
                    </button>
                    {s.id === 'facebook' && (
                      <ol className='og-howto'>
                        <li>לוחצים על הכפתור</li>
                        <li>מזינים אימייל או טלפון וסיסמה של הפייסבוק</li>
                        <li>אם מגיע קוד אימות — מזינים גם אותו</li>
                      </ol>
                    )}
                    </>)}
                    <div className='og-links'>
                      {watching === s.id && (
                        <button onClick={() => watch(s.id, true)}>
                          <i className='bi bi-arrow-counterclockwise' /> צפייה חוזרת
                        </button>
                      )}
                      <button onClick={() => help(s.id)}>
                        <i className='bi bi-whatsapp' /> צריך עזרה?
                      </button>
                      <button onClick={() => ticket(s.id)}>
                        <i className='bi bi-life-preserver' /> פנייה לתמיכה
                      </button>
                    </div>
                    <div className='og-auto'>✓ השלב יסומן לבד ברגע שזה בוצע</div>
                  </div>
                )}
              </div>
            )
          })}
          <a className='og-full' href='/tutorial-demo' target='_blank' rel='noreferrer'>
            רוצים לראות את כל המדריך? ←
          </a>
        </div>
      )}
    </div>
  )
}

export default OnboardingGuide
